import {DataCacheStatus, useDataCache} from "@/lib/datacache";
import {cartonGradeApi, fefcoCodeApi, i18nTextApi, legalFormApi, plantApi, tenantsApi, userApi} from "@/boxhub-api";
import {RequestParams} from "@/api/http-client";
import {useCallback, useMemo} from "react";
import {
    CartonGradeCatalogDTO,
    CompanyDisplayDTO,
    CompanyStatus,
    Country,
    Language,
    LegalForm,
    NumberOfPallet,
    OrderStatus,
    PalletType,
    PlantDTO,
    PrintType,
    QuoteStatus,
    RequestStatus,
    Salutation,
    TerminationSource,
    UserDisplayDTO
} from "@/api/data-contracts";

export const useFefcoCodes = (): [string[], DataCacheStatus] => {
    const cache = useDataCache("fefco-codes", fefcoCodeApi.getFefcoCodes);
    const fefcoCodes = useMemo<string[]>(
        () => cache[0]?.map((fefcoCode) => fefcoCode.code as string).filter(Boolean) || [],
        [cache]
    );
    return [fefcoCodes, cache[1]];
};

export const useCartonGrades = (): [CartonGradeCatalogDTO[], DataCacheStatus] =>
    useDataCache("carton-grades", cartonGradeApi.getCartonGrades);

export const useEmployees = (): [UserDisplayDTO[], DataCacheStatus] =>
    useDataCache("employees", userApi.getActiveCompanyUsers);

export const useTenantCompanies = (): [CompanyDisplayDTO[], DataCacheStatus] =>
    useDataCache("tenant-companies", tenantsApi.getCurrentTenantCompanies);
export const usePlants = (): [PlantDTO[], DataCacheStatus] => useDataCache("plants", plantApi.getAllPlants);

/**
 * Languages with at least one maintained/uploaded language file (enum declaration order).
 * Used by every user-selectable language dropdown: the profile dropdown (`useUserEditSchema`,
 * `UserPage`), the invitation dialogs (`useUserInvitationSchema`/`UserInvitationDialog`,
 * `useCompanyInvitationSchema`/`CompanyInvitationDialog`) and the company sign-up form
 * (`useSignUpSchema`, `UserSignUpForm`). The static `languageOptions` export below is only the
 * empty-cache fallback (baseline GERMAN) these dropdowns use while `useAvailableLanguages` is
 * still loading or has errored.
 */
export const useAvailableLanguages = (): [Language[], DataCacheStatus] =>
    useDataCache("available-languages", i18nTextApi.getAvailableLanguages);

/**
 * Reference cache for the country-to-legal-form mapping (GET /api/legal-forms/by-country).
 * The endpoint returns a single map keyed by country, not a list — useDataCache expects an array-shaped
 * payload for its caching/loading-state machinery, so the loader wraps the map in a single-element array
 * and this hook unwraps it again, defaulting to an empty map before the reference data has loaded.
 *
 * The loader is wrapped in useCallback with an empty dependency array so useDataCache (which lists
 * `loader` in its effect's dependency array) receives a stable reference across renders — without this,
 * a fresh inline arrow was created on every render, re-running the fetch effect on every render. Because
 * this endpoint returns no ETag, every re-run triggered a new fetchData() call, making the reported
 * DataCacheStatus oscillate "loading" <-> "loaded" instead of settling once after the initial mount.
 */
export const useLegalFormsByCountry = (): [Record<string, LegalForm[]>, DataCacheStatus] => {
    const loadLegalFormsByCountry = useCallback(
        (params?: RequestParams, headers?: Record<string, string>) =>
            legalFormApi
                .getLegalFormsByCountry({ ...params, headers })
                .then((response) => ({ ...response, data: [response.data] })),
        []
    );
    const cache = useDataCache<Record<string, LegalForm[]>>("legal-forms-by-country", loadLegalFormsByCountry);
    const legalFormsByCountry = useMemo<Record<string, LegalForm[]>>(() => cache[0]?.[0] ?? {}, [cache]);
    return [legalFormsByCountry, cache[1]];
};

export const palletTypeOptions = [
    PalletType.EURO_PALLET_NEW,
    PalletType.EURO_PALLET_NW,
    PalletType.EURO_PALLET_USED,
    PalletType.INDUSTRY_PALLET,
    PalletType.ONE_WAY_PALLET,
    PalletType.EURO_PALLET_ONE_WAY,
    PalletType.INDUSTRY_PALLET_ONE_WAY
];

export const printOptions = [PrintType.NO_PRINT, PrintType.WARNING_PRINT, PrintType.FULL_AREA_PRINT];

export const numberOfPalletLabels = [
    NumberOfPallet.ONE_ON_THE_SIDE,
    NumberOfPallet.ONE_ON_THE_LONG_AND_SHORT_SIDE,
    NumberOfPallet.ON_ALL_FOUR_SIDES
];

export const salutationOptions = [
    Salutation.MR,
    Salutation.MS,
    Salutation.DR_MALE,
    Salutation.DR_FEMALE,
    Salutation.PROF_MALE,
    Salutation.PROF_FEMALE
];

export const companyStatusOptions = [
    CompanyStatus.OPERATOR,
    CompanyStatus.INVITED,
    CompanyStatus.UNVERIFIED,
    CompanyStatus.CONFIRMED,
    CompanyStatus.REJECTED,
    CompanyStatus.CONFIRMED_LOCKED,
    CompanyStatus.TERMINATION_RECEIVED,
    CompanyStatus.TERMINATION_RECEIVED_LOCKED,
    CompanyStatus.TERMINATED
];

export const quoteStatusOptions = [
    QuoteStatus.VACANT,
    QuoteStatus.SUBMITTED,
    QuoteStatus.IN_NEGOTIATION,
    QuoteStatus.REJECTED,
    QuoteStatus.ACCEPTED,
    QuoteStatus.DISMISSED,
    QuoteStatus.NOT_VACANT
];

/**
 * `DRAFT` never yields a hit — the status only exists on transient DTOs (`RequestService#doGetTemplate`,
 * `RequestService#makeClone`) and is never persisted, so it is filtered by "Phase 0, Entscheidung 3" as a
 * harmless selection value with no matching row.
 */
export const requestStatusOptions = [
    RequestStatus.DRAFT,
    RequestStatus.CREATED,
    RequestStatus.PUBLISHED,
    RequestStatus.TO_BE_CONFIRMED,
    RequestStatus.IN_NEGOTIATION,
    RequestStatus.CONFIRMED,
    RequestStatus.CANCELLED,
    RequestStatus.DEACTIVATED
];

/**
 * Default request status selection for the internal request overview: every value of
 * `requestStatusOptions`, so cancelled and deactivated requests are visible from the start (see "Standing
 * constraints" in the #1606 plan). Users who already stored a selection keep theirs — this value only
 * applies without one.
 */
export const initialRequestStatuses: RequestStatus[] = [...requestStatusOptions];

/**
 * Default quote status selection for the external request overview and the bidder overview: every value
 * of `quoteStatusOptions`. Same rationale as `initialRequestStatuses` above.
 */
export const initialQuoteStatuses: QuoteStatus[] = [...quoteStatusOptions];

/** Full dropdown universe for order status filters. */
export const orderStatusOptions: OrderStatus[] = Object.values(OrderStatus);

/**
 * Default order status selection: every order status, so cancelled and deactivated orders are visible
 * from the start. Users who already stored a selection keep theirs — this value only applies without one.
 */
export const initialOrderStatuses: OrderStatus[] = Object.values(OrderStatus);

export const languageOptions = [Language.GERMAN];

export const countryOptions = [
    Country.GERMANY,
    Country.NETHERLANDS,
    Country.AUSTRIA,
    Country.SWITZERLAND,
    Country.CZECHIA,
    Country.POLAND,
    Country.ITALY,
    Country.FRANCE
];

export const terminationInitiatorOptions = [TerminationSource.CLIENT, TerminationSource.OPERATOR];

export const legalFormOptions = [
    LegalForm.AG,
    LegalForm.AG_KG,
    LegalForm.AGCoKGaA,
    LegalForm.AGCoOHG,
    LegalForm.GAG,
    LegalForm.GbR,
    LegalForm.GmbH,
    LegalForm.GmbHCoKG,
    LegalForm.GmbHCoKGaA,
    LegalForm.GmbHCoOHG,
    LegalForm.GmbHG,
    LegalForm.KG,
    LegalForm.KGaA,
    LegalForm.KGaACoKG,
    LegalForm.OHG,
    LegalForm.OHGMbh,
    LegalForm.PartG,
    LegalForm.PartGMbB,
    LegalForm.Sonstige,
    LegalForm.StiftungCoKG,
    LegalForm.UGH,
    LegalForm.UGHCoKG,
    LegalForm.SE,
    LegalForm.Einzelunternehmer,
    LegalForm.EingetragenesUnternehmen,
    LegalForm.BeslotenVennootschap,
    LegalForm.NaamlozeVennootschap,
    LegalForm.VennootschapOnderFirma,
    LegalForm.CommanditaireVennootschap,
    LegalForm.Maatschap,
    LegalForm.Eenmanszaak,
    LegalForm.CHKollektivgesellschaft,
    LegalForm.CHKommanditgesellschaft,
    LegalForm.CHEinzelunternehmen,
    LegalForm.CHGenossenschaft,
    LegalForm.CZSRO,
    LegalForm.CZAS,
    LegalForm.CZKS,
    LegalForm.CZVOS,
    LegalForm.CZDruzstvo,
    LegalForm.CZ_OSVC,
    LegalForm.PLSpZOO,
    LegalForm.PL_S_A,
    LegalForm.PLSpK,
    LegalForm.PLSpJ,
    LegalForm.PLSpP,
    LegalForm.PL_PSA,
    LegalForm.PL_JDG,
    LegalForm.ITSRL,
    LegalForm.ITSRLS,
    LegalForm.ITSPA,
    LegalForm.ITSAPA,
    LegalForm.ITSNC,
    LegalForm.ITSAS,
    LegalForm.ITSocCoop,
    LegalForm.ITDittaIndividuale,
    LegalForm.FR_SARL,
    LegalForm.FR_EURL,
    LegalForm.FR_SAS,
    LegalForm.FR_SASU,
    LegalForm.FR_SA,
    LegalForm.FR_SNC,
    LegalForm.FR_SCS,
    LegalForm.FR_SCA,
    LegalForm.FR_EI
];
